import React, { FC, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { JourneyFilterState, JourneyMeta } from '../../../../models/journey-explorer/journey-explorer';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import {
	MAX_GROUPS,
	addGroup,
	clearSimplify,
	groupLabelError,
	groupOf,
	isSimplified,
	removeGroup,
	simplifyOf,
	toggleHidden,
} from '../journey-simplify';
import { Dot } from './filter-group';

interface SimplifyControlsProps {
	meta: JourneyMeta;
	lookup: Map<string, InteractionInfo>;
	filterState: JourneyFilterState;
	locked: string[];
	onChange: (state: JourneyFilterState) => void;
}

const SimplifyControls: FC<SimplifyControlsProps> = ({ meta, lookup, filterState, locked, onChange }) => {
	const simplify = simplifyOf(filterState);
	const [draft, setDraft] = useState<{ label: string; names: string[] } | null>(null);
	const names = meta.interaction_types.flatMap((type) => type.interactions.map((interaction) => interaction.name));
	const ungrouped = names.filter((name) => groupOf(simplify, name) === null);
	const draftError = draft ? groupLabelError(meta, simplify, draft.label, draft.names) : null;

	const toggleDraftName = (name: string) =>
		setDraft(
			(current) =>
				current && {
					...current,
					names: current.names.includes(name)
						? current.names.filter((item) => item !== name)
						: [...current.names, name],
				}
		);

	const saveGroup = () => {
		if (draft && !draftError) {
			onChange(addGroup(filterState, draft.label.trim(), draft.names));
			setDraft(null);
		}
	};

	return (
		<div className={styles.simplify}>
			<div className={styles.simplify_label}>Group interactions</div>
			{simplify.groups.map((group) => (
				<div key={group.label} className={styles.simplify_group}>
					<span className={styles.simplify_group_text}>
						<b>{group.label}</b>
						{group.names.map((name) => resolveInfo(lookup, name).label).join(', ')}
					</span>
					<button
						type="button"
						className={styles.simplify_remove}
						aria-label={`Ungroup ${group.label}`}
						title="Ungroup"
						onClick={() => onChange(removeGroup(filterState, group.label))}
					>
						✕
					</button>
				</div>
			))}
			{draft ? (
				<div className={styles.simplify_draft}>
					<input
						className={styles.simplify_input}
						aria-label="Group name"
						placeholder="Group name, for example Email clicks"
						value={draft.label}
						maxLength={60}
						onChange={(event) => setDraft({ ...draft, label: event.target.value })}
					/>
					<div className={styles.simplify_chips}>
						{ungrouped.map((name) => {
							const info = resolveInfo(lookup, name);
							const active = draft.names.includes(name);
							return (
								<button
									key={name}
									type="button"
									aria-pressed={active}
									className={`${styles.simplify_chip} ${active ? styles.simplify_chip_on : ''}`}
									onClick={() => toggleDraftName(name)}
								>
									<Dot color={info.color} size={6} />
									{info.label}
								</button>
							);
						})}
					</div>
					{draftError && <span className={styles.flow_controls_note}>{draftError}</span>}
					<div className={styles.simplify_actions}>
						<button
							type="button"
							className={styles.compare_apply}
							disabled={draftError !== null}
							onClick={saveGroup}
						>
							Group
						</button>
						<button type="button" className={styles.compare_cancel} onClick={() => setDraft(null)}>
							Cancel
						</button>
					</div>
				</div>
			) : (
				simplify.groups.length < MAX_GROUPS && (
					<button
						type="button"
						className={styles.simplify_link}
						onClick={() => setDraft({ label: '', names: [] })}
					>
						+ New group
					</button>
				)
			)}
			<div className={styles.simplify_label}>Hide interactions</div>
			<div className={styles.simplify_chips}>
				{ungrouped.map((name) => {
					const info = resolveInfo(lookup, name);
					const hidden = simplify.hidden.includes(name);
					const isLocked = locked.includes(name);
					return (
						<button
							key={name}
							type="button"
							aria-pressed={hidden}
							disabled={isLocked && !hidden}
							title={isLocked ? 'Used as the Flow start or end point' : undefined}
							className={`${styles.simplify_chip} ${hidden ? styles.simplify_chip_hidden : ''}`}
							onClick={() => onChange(toggleHidden(filterState, name))}
						>
							<Dot color={info.color} size={6} />
							{info.label}
						</button>
					);
				})}
			</div>
			<span className={styles.flow_controls_note}>
				Hidden interactions are skipped, so the steps either side join up.
			</span>
			{isSimplified(simplify) && (
				<button
					type="button"
					className={styles.simplify_link}
					onClick={() => onChange(clearSimplify(filterState))}
				>
					Show everything
				</button>
			)}
		</div>
	);
};

export default SimplifyControls;
